import { useEffect, useMemo } from 'react';
import { useQueries } from 'react-query';

import { actionDetailType, searchPriceKeywordSource } from '@common/const';
import { getOsType } from '@common/webview';

import useRouterHook from '@lib/hook/useRouterHook';

import {
  getSearchPriceMainInfo,
  getSearchPriceProduct,
  getSearchPriceRecommendKeyword,
} from '@api/standardPrice.ts/search';

/** 시세조회에 필요한 데이터 관리
 * @return {string} priceKeyword
 */
const useSearchPriceData = () => {
  const {
    queryString: {
      searchWord,
      productSeq,
      actionDetailType: actionDetailTypeQuery,
      keywordSource: keywordSourceQuery,
      transactionId: transactionIdQuery,
      exposureKeyword: exposureKeywordQuery,
    },
  } = useRouterHook();
  const priceKeyword = searchWord || '';
  let transactionId: string | null = `${transactionIdQuery ?? ''}`;
  let exposureKeyword: string | null | undefined = `${
    exposureKeywordQuery ?? ''
  }`;

  const actionType = useMemo(() => {
    const type = String(actionDetailTypeQuery) as keyof typeof actionDetailType;

    if (type in actionDetailType) {
      return actionDetailType[type];
    }

    return actionDetailType.MARKET_PRICE_CAFE;
  }, [actionDetailTypeQuery]);

  const keywordSource = useMemo(() => {
    const keyword = keywordSourceQuery as keyof typeof searchPriceKeywordSource;

    if (!keyword) return searchPriceKeywordSource.INPUT_KEYWORD;
    if (keyword in searchPriceKeywordSource) {
      return searchPriceKeywordSource[keyword];
    }

    return searchPriceKeywordSource.INPUT_KEYWORD;
  }, [keywordSourceQuery]);

  const [{ data: recommendData }, { data: mainData }, { data: productData }] =
    useQueries([
      {
        queryKey: ['recommend'],
        queryFn: getSearchPriceRecommendKeyword,
      },
      {
        queryKey: ['main', priceKeyword],
        queryFn: () =>
          getSearchPriceMainInfo(
            priceKeyword,
            keywordSource,
            getOsType(),
            actionType,
            `${transactionId}`,
          ),
        enabled: !!priceKeyword && actionType.api === 'MAIN',
      },
      {
        queryKey: ['exceptMain', priceKeyword, actionType],
        queryFn: () =>
          getSearchPriceProduct(
            productSeq,
            keywordSource,
            getOsType(),
            actionType,
            `${transactionId}`,
          ),
        enabled: !!priceKeyword && actionType.api === 'PRODUCT',
      },
    ]);

  const recommendInfo = recommendData?.data;
  const mainInfo = mainData?.data;
  const productInfo = productData?.data;

  useEffect(() => {
    if (productData) {
      transactionId = productData.data.transactionId;
      exposureKeyword =
        productData.data.standardPriceMainResult?.selectExposureKeyword ?? '';
    } else if (mainData) {
      transactionId = mainData.data.transactionId;
      exposureKeyword = mainData.data.selectExposureKeyword ?? '';
    }
  }, []);

  return useMemo(() => {
    return {
      priceKeyword,
      actionType,
      keywordSource,
      transactionId,
      exposureKeyword,
      searchInfo:
        productInfo?.standardPriceMainResult ??
        mainInfo ??
        productInfo?.standardPriceMain,
      recommendInfo,
    };
  }, [
    priceKeyword,
    actionType,
    keywordSource,
    transactionId,
    exposureKeyword,
    mainInfo,
    productInfo,
    recommendInfo,
  ]);
};

export default useSearchPriceData;
